<template>
  <v-card class="pa-4">
    <h2>Received Assignments by Insurer (Month)</h2>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedClass"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!selectedMonth"
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :loading="exporting"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <div class="mb-4">
        <v-alert
          type="success"
          outlined
          prominent
          border="left"
          color="primary"
          class="text-h5 font-weight-bold"
        >
          Total Files: {{ totals.total }}
        </v-alert>
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th>Insurer</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in reportData">
            <tr :key="row.insurerCode">
              <td>
                <strong>{{ row.insurerCode }}</strong>
              </td>
              <td>{{ row.total }}</td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.insurerCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.insurerCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.total }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select department, classification, and month, then click "Generate
      Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ReceivedMonthInsurer",
  data() {
    return {
      selectedDept: "all",
      deptOptions: [],
      selectedClass: "all",
      classOptions: [],
      filteredClassOptions: [],
      selectedMonth: null,
      menuMonth: false,
      reportData: [],
      totals: {
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
    };
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
  },
  methods: {
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [
        { id: "all", name: "ALL DEPARTMENTS" },
        ...res.data,
      ].sort((a, b) => a.name.localeCompare(b.name));
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      // Always ensure ALL CLASSIFICATIONS is present and at the top
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ].sort((a, b) => {
        if (a.id === "all") return -1;
        if (b.id === "all") return 1;
        return a.subCode.localeCompare(b.subCode);
      });
    },
    filterClassifications(deptId) {
      // Only show ALL CLASSIFICATIONS if ALL DEPARTMENTS is selected
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        // Filter for selected department, but always include ALL CLASSIFICATIONS
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        total: 0,
      };
      this.noDataMessage = "";
      try {
        // Ensure month is always a "YYYY-MM" string
        let monthStr = "";
        if (
          typeof this.selectedMonth === "string" &&
          /^\d{4}-\d{2}$/.test(this.selectedMonth)
        ) {
          monthStr = this.selectedMonth;
        } else if (this.selectedMonth) {
          monthStr = moment(this.selectedMonth).format("YYYY-MM");
        }
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          month: monthStr,
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/received-month-insurer`,
          { params }
        );
        this.processReportData(res.data);
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const monthDisplay = monthStr
            ? moment(monthStr, "YYYY-MM").format("MMMM YYYY")
            : "";
          this.noDataMessage = `No received files found for ${monthDisplay}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      let total = 0;
      this.reportData.forEach((row) => {
        total += row.total;
      });
      this.totals.total = total;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        // Ensure month is always a "YYYY-MM" string
        let monthStr = "";
        if (
          typeof this.selectedMonth === "string" &&
          /^\d{4}-\d{2}$/.test(this.selectedMonth)
        ) {
          monthStr = this.selectedMonth;
        } else if (this.selectedMonth) {
          monthStr = moment(this.selectedMonth).format("YYYY-MM");
        }
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          month: monthStr,
        };
        const url =
          `${apiConfig.baseURL}/compliance/received-month-insurer/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loadDepartments();
    this.loadClassifications();
  },
};
</script>
